import React, { useState, useEffect } from 'react'
import { useSystemPrompts } from '@/hooks/use-system-prompts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import {
  Save,
  RefreshCw,
  Database,
  Edit,
  FileText,
  Code,
  Hash,
  AlertCircle
} from 'lucide-react'

export function SystemPromptsAdmin() {
  const { prompts, loading, error, updatePrompt, refreshPrompts } = useSystemPrompts()
  const [selectedPrompt, setSelectedPrompt] = useState<string>('')
  const [editingContent, setEditingContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    checkAdminStatus()
  }, [])

  useEffect(() => {
    if (prompts.length > 0 && !selectedPrompt) {
      setSelectedPrompt(prompts[0].name)
      setEditingContent(prompts[0].content)
    }
  }, [prompts])

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    setIsAdmin(user?.email === 'admin@mission-solar.at' || user?.email?.includes('admin') || false)
  }

  const handlePromptSelect = (name: string) => {
    const prompt = prompts.find(p => p.name === name)
    if (prompt) {
      setSelectedPrompt(name)
      setEditingContent(prompt.content)
    }
  }

  const handleSave = async () => {
    if (!isAdmin) {
      toast({
        title: 'Keine Berechtigung',
        description: 'Nur Administratoren können Prompts speichern',
        variant: 'destructive'
      })
      return
    }

    setSaving(true)
    try {
      await updatePrompt(selectedPrompt, editingContent)
      setEditDialogOpen(false)
      toast({
        title: 'Prompt gespeichert',
        description: `${selectedPrompt} wurde erfolgreich aktualisiert`,
      })
    } catch (err) {
      console.error('Error saving prompt:', err)
    } finally {
      setSaving(false)
    }
  }

  const currentPrompt = prompts.find(p => p.name === selectedPrompt)

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'extractor': return <Hash className="h-4 w-4" />
      case 'refiner': return <Edit className="h-4 w-4" />
      case 'emailstyler': return <FileText className="h-4 w-4" />
      default: return <Code className="h-4 w-4" />
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'extractor': return 'bg-blue-100 text-blue-800'
      case 'refiner': return 'bg-green-100 text-green-800'
      case 'emailstyler': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Zugriff verweigert
            </CardTitle>
            <CardDescription>
              Nur Administratoren können System Prompts verwalten.
              Bitte melden Sie sich mit admin@mission-solar.at an.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Lade System Prompts...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Fehler beim Laden</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={refreshPrompts}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-light tracking-tight flex items-center gap-2">
            <Database className="h-6 w-6" />
            System Prompts
          </h1>
          <p className="text-muted-foreground mt-1">
            Verwalten Sie die AI System Prompts für Email-Verarbeitung
          </p>
        </div>
        <Button onClick={refreshPrompts} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Prompt List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Verfügbare Prompts</CardTitle>
            <CardDescription>
              {prompts.length} aktive Prompts
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {prompts.map((prompt) => (
                <button
                  key={`${prompt.name}-${prompt.version}`}
                  onClick={() => handlePromptSelect(prompt.name)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    selectedPrompt === prompt.name ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{prompt.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {prompt.description}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getCategoryColor(prompt.category)}>
                          {getCategoryIcon(prompt.category)}
                          <span className="ml-1">{prompt.category}</span>
                        </Badge>
                        <Badge variant="outline">
                          {prompt.return_type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Prompt Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{currentPrompt?.name}</CardTitle>
                <CardDescription>
                  {currentPrompt?.description}
                </CardDescription>
              </div>
              <Button onClick={() => setEditDialogOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Bearbeiten
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="content">Inhalt</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>
              <TabsContent value="content" className="space-y-4">
                <div className="rounded-lg border bg-muted/30 p-4">
                  <pre className="text-sm whitespace-pre-wrap font-mono">
                    {currentPrompt?.content || 'Kein Prompt ausgewählt'}
                  </pre>
                </div>
              </TabsContent>
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kategorie</Label>
                    <p className="text-sm mt-1">
                      <Badge className={getCategoryColor(currentPrompt?.category || '')}>
                        {currentPrompt?.category}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <Label>Rückgabetyp</Label>
                    <p className="text-sm mt-1">
                      <Badge variant="outline">{currentPrompt?.return_type}</Badge>
                    </p>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <p className="text-sm mt-1">
                      <Badge variant={currentPrompt?.is_active ? 'default' : 'secondary'}>
                        {currentPrompt?.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <Label>Zuletzt aktualisiert</Label>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {currentPrompt?.updated_at
                        ? new Date(currentPrompt.updated_at).toLocaleString('de-DE')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Prompt bearbeiten: {selectedPrompt}</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie den Inhalt des System Prompts.
              Änderungen werden sofort wirksam.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4">
            <Textarea
              value={editingContent}
              onChange={(e) => setEditingContent(e.target.value)}
              className="font-mono text-sm min-h-[400px]"
              placeholder="Prompt Inhalt..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setEditingContent(currentPrompt?.content || '')
              }}
            >
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Speichere...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}